/**
 * Como se cobra un producto.
 *
 * POR QUE ESTE MODULO EXISTE
 * --------------------------
 * `unit` parece un campo de catalogo y no lo es: es la decision que le da
 * significado a todo lo demas del producto. El precio pasa a ser por kilo o por
 * pieza, la caja pregunta el peso o suma de uno en uno, la existencia se cuenta
 * o se pesa, y la merma natural aplica o no aplica.
 *
 * El alta la traia siempre en "Pieza" y sin ninguna pista. En una tienda de
 * abarrotes eso esta al reves de la realidad: el granel, la fruta, la verdura,
 * la cremeria y la tortilla se pesan, y son justo los productos que mas se dan
 * de alta a mano porque no traen codigo de barras. Se comprobo en la base: en
 * la categoria "Granel" hay un producto capturado por PIEZA con 50 piezas por
 * caja. Nadie lo hizo por descuido: el formulario nunca pregunto.
 *
 * Aqui vive el conocimiento del ramo para proponer la unidad correcta. Tres
 * reglas de diseño, heredadas de los motores de inteligencia (CLAUDE.md §11):
 *
 *   1. Es DETERMINISTA. Mismo nombre y misma categoria, misma propuesta.
 *   2. Toda propuesta trae su RAZON en español, con los numeros que la
 *      produjeron. Nadie confia en un formulario que decide solo y no dice
 *      por que.
 *   3. Si no hay señal, se ABSTIENE (`null`) y la pantalla deja lo que haya.
 *      Adivinar mal es peor que no proponer: mueve el precio de lugar.
 *
 * Nada de esto valida: la unidad la sigue validando la base. Esto propone.
 */
import { divRoundHalfUp, formatQty, MILLI, parseMoneyToCents, parseQtyToMilli, qtyMilliFromMoneyCents, } from './money.js';
import { UNIT_SHORT, UNIT_SPOKEN, unitIsWeighed, } from './schemas.js';
export const UNIT_FAMILY = {
    pz: 'count',
    paq: 'count',
    caja: 'count',
    kg: 'weight',
    g: 'weight',
    l: 'volume',
    ml: 'volume',
    /*
     * La bodega. Un BULTO se cuenta —se despacha cerrado, no se abre para sacar
     * medio— asi que es de la familia de la pieza aunque adentro haya 50 kg; su
     * peso vive en el nombre de la unidad y no en la cantidad. La TONELADA se
     * pesa: se venden 1.5 y se venden 0.75, y una milesima de tonelada es
     * exactamente un kilo, que es la precision con la que se carga un camion.
     */
    ton: 'weight',
    b25: 'count',
    b50: 'count',
};
export const UNITS_BY_FAMILY = {
    count: ['pz', 'paq', 'caja', 'b25', 'b50'],
    weight: ['kg', 'g', 'ton'],
    volume: ['l', 'ml'],
};
/** Como se le llama a la familia en el formulario. */
export const FAMILY_LABELS = {
    count: 'Por pieza',
    weight: 'Por peso',
    volume: 'Por medida',
};
/** Que hace la caja con cada familia. Es la consecuencia, no el nombre. */
export const FAMILY_CONSEQUENCE = {
    count: 'La caja suma de uno en uno.',
    weight: 'La caja pregunta el peso cada vez.',
    volume: 'La caja pregunta cuanto lleva cada vez.',
};
/** Plural, para etiquetas como "Cuanto tienes ahora (kilos)". */
export const UNIT_PLURAL = {
    pz: 'piezas',
    kg: 'kilos',
    g: 'gramos',
    l: 'litros',
    ml: 'mililitros',
    paq: 'paquetes',
    caja: 'cajas',
    ton: 'toneladas',
    b25: 'bultos de 25 kg',
    b50: 'bultos de 50 kg',
};
/**
 * Genero gramatical de la unidad, para concordar las etiquetas que se arman
 * con el plural: "Cuantas piezas trae la caja" contra "Cuantos paquetes trae
 * la caja". Sin esto la pantalla escribe "Cuantas paquetes", que se lee como
 * un error de la aplicacion aunque todo lo demas este bien.
 */
export const UNIT_GENDER = {
    pz: 'f',
    kg: 'm',
    g: 'm',
    l: 'm',
    ml: 'm',
    paq: 'm',
    caja: 'f',
    ton: 'f',
    b25: 'm',
    b50: 'm',
};
/** "Cuantas" o "Cuantos" segun la unidad. */
export function howMany(unit) {
    return UNIT_GENDER[unit] === 'f' ? 'Cuantas' : 'Cuantos';
}
/** Unidad por omision de cada familia, la que usa el 99 % de las tiendas. */
export const FAMILY_DEFAULT_UNIT = {
    count: 'pz',
    weight: 'kg',
    volume: 'l',
};
/**
 * Merma natural mensual sugerida, en puntos base, por familia.
 *
 * No es un dato de precision: es un punto de partida editable para que el
 * campo no nazca en cero por descuido, que es lo que convierte la
 * deshidratacion normal de un costal en un "faltante" que no existio.
 */
export const SUGGESTED_SHRINKAGE_BP = {
    count: 0,
    weight: 200,
    volume: 100,
};
export function unitFamily(unit) {
    return UNIT_FAMILY[unit];
}
/**
 * Cantidad con su unidad: "0.75 kg", "3 pz", "200 b50", "1.5 t".
 *
 * Vive aqui y no en `money.ts` porque necesita `UNIT_SHORT`, y `money.ts` no
 * importa nada: es la capa de los enteros. Es tambien el unico lugar donde se
 * pega una unidad a un numero, de modo que la abreviatura se decide una vez.
 *
 * `isWeighed` se puede omitir y entonces se deduce de la unidad. Se deja pasar
 * porque el renglon de una venta guarda su propia bandera y esa es la verdad de
 * ese ticket, aunque el producto haya cambiado de unidad despues.
 */
export function formatQtyWithUnit(qtyMilli, unit, isWeighed) {
    return `${formatQty(qtyMilli, isWeighed ?? unitIsWeighed(unit))} ${UNIT_SHORT[unit]}`;
}
// ---------------------------------------------------------------------------
// Que se cobra en cada tipo de sucursal
// ---------------------------------------------------------------------------
/**
 * Las unidades de cada perfil.
 *
 * Una tienda de abarrotes y una bodega no comparten NINGUNA: la primera despacha
 * lo que el cliente pide —una pieza, medio kilo— y la segunda entrega bulto
 * cerrado o camion. Un catalogo que mezclara las dos dejaria al dueño leyendo
 * "180" sin saber si son piezas o bultos de 50 kg, que es media tonelada de
 * diferencia por renglon.
 */
export const UNITS_BY_PROFILE = {
    retail: ['pz', 'kg', 'g', 'l', 'ml', 'paq', 'caja'],
    bulk: ['ton', 'b25', 'b50'],
};
export function unitsForProfile(profile) {
    return UNITS_BY_PROFILE[profile];
}
export function unitAllowedIn(profile, unit) {
    return UNITS_BY_PROFILE[profile].includes(unit);
}
/**
 * Con que unidad nace una ficha en blanco.
 *
 * En la bodega es el bulto de 50 kg y no la tonelada: es lo que mas se mueve, y
 * si la propuesta falla lo que queda puesto tiene que ser lo probable.
 */
export const DEFAULT_UNIT_BY_PROFILE = {
    retail: 'pz',
    bulk: 'b50',
};
export function defaultUnitFor(profile) {
    return DEFAULT_UNIT_BY_PROFILE[profile];
}
/**
 * Acota una unidad al perfil. Lo que ya no cabe cae a la de omision.
 *
 * Hace falta porque un borrador guardado sobrevive a un cambio de tipo de
 * sucursal: sembrar la ficha con una unidad que la base va a rechazar es
 * regalarle a alguien un formulario que solo puede fallar al guardar.
 */
export function clampUnitToProfile(profile, unit) {
    return unitAllowedIn(profile, unit) ? unit : defaultUnitFor(profile);
}
const ENTRY_EQUIV = {
    kg: [{ unit: 'g', num: 1, den: 1000 }],
    l: [{ unit: 'ml', num: 1, den: 1000 }],
    ton: [{ unit: 'kg', num: 1, den: 1000 }],
    b50: [{ unit: 'ton', num: 20, den: 1 }],
    b25: [{ unit: 'ton', num: 40, den: 1 }],
};
/**
 * La unidad CHICA, o `null` si esta unidad no tiene.
 *
 * Chica de verdad: la que vale una fraccion de la del precio. La tonelada es
 * alternativa del bulto pero no es su unidad chica, y la diferencia importa
 * porque de esto cuelga el aviso de "¿no seran gramos?" cuando alguien teclea
 * una cantidad sospechosamente grande. Cien bultos no son sospechosos.
 */
export function subUnitOf(unit) {
    return ENTRY_EQUIV[unit]?.find((e) => e.num < e.den)?.unit ?? null;
}
/**
 * Las unidades en las que se puede teclear una cantidad de este producto,
 * empezando siempre por la del precio.
 *
 * Un solo elemento significa que no hay nada que elegir y la pantalla no debe
 * dibujar el selector: un control con una sola opcion es ruido que ademas hace
 * dudar de si falta algo.
 */
export function entryUnitsFor(unit) {
    const equiv = ENTRY_EQUIV[unit];
    return equiv ? [unit, ...equiv.map((e) => e.unit)] : [unit];
}
/**
 * Lo tecleado -> milesimas de la unidad del PRECIO, que es la unica cantidad
 * que el resto del sistema entiende.
 *
 * La conversion es EXACTA y no toca punto flotante en ningun paso: se multiplica
 * por el numerador y se divide una sola vez, half-up, la misma regla que el
 * dinero. Como `MILLI` es mil y en un kilo hay mil gramos, el numero de gramos ES
 * el numero de milesimas: teclear `250` en gramos y `0.25` en kilos produce el
 * mismo entero, bit a bit. Y una tonelada de un producto en bultos de 50 da
 * 20,000 milesimas, o sea veinte bultos clavados.
 *
 * Los decimales de gramo —que la caja no deja teclear, porque un miligramo de
 * queso no existe en el mostrador— se redondean una sola vez.
 *
 * Devuelve `null` si el texto no es una cantidad, o si `typedIn` no es una de
 * las unidades que `entryUnitsFor` permite para `priced`. Nunca adivina.
 */
export function qtyMilliFromEntry(text, priced, typedIn) {
    const typedMilli = parseQtyToMilli(text);
    if (typedMilli === null)
        return null;
    if (typedIn === priced)
        return typedMilli;
    const equiv = ENTRY_EQUIV[priced]?.find((e) => e.unit === typedIn);
    if (!equiv)
        return null;
    return divRoundHalfUp(typedMilli * equiv.num, equiv.den);
}
/**
 * La vuelta: milesimas de la unidad del PRECIO -> el numero que se teclearia en
 * `typedIn`. Devuelve `null` si esa unidad no es de las que se pueden teclear.
 *
 * Existe para las PISTAS del historial, que llegan en milesimas y hay que
 * pintarlas como atajos en el modo que este elegido. No es un segundo criterio
 * de conversion: sale de la misma tabla que `qtyMilliFromEntry`, y por eso las
 * dos siguen coincidiendo si algun dia cambia una equivalencia.
 *
 * Devuelve un numero con decimales a proposito: es una ETIQUETA para el ojo. La
 * cantidad que de verdad se cobra vuelve a pasar por `qtyMilliFromEntry`, que es
 * exacta; aqui un decimal de mas solo cambiaria como se lee un boton.
 */
export function entryQtyFromMilli(qtyMilli, priced, typedIn) {
    if (typedIn === priced)
        return qtyMilli / MILLI;
    const equiv = ENTRY_EQUIV[priced]?.find((e) => e.unit === typedIn);
    if (!equiv)
        return null;
    return (qtyMilli * equiv.den) / (equiv.num * MILLI);
}
// ---------------------------------------------------------------------------
// La otra forma de decir cuanto: A DINERO
// ---------------------------------------------------------------------------
/**
 * Capturar por IMPORTE en vez de por cantidad.
 *
 * "Dame cinco pesos de chile" es una venta de mostrador completa, y hasta ahora
 * obligaba a resolverla de cabeza: dividir cinco entre el precio del kilo,
 * redondear a algo que se pueda pesar y teclearlo, con el cliente enfrente. El
 * resultado de esa cuenta mental es un numero distinto en cada turno y casi
 * nunca cobra los cinco pesos que pidieron.
 *
 * NO ES UNA UNIDAD, y por eso no entra en `Unit`. `Unit` es lo que decide como
 * se cobra el producto —vive en el catalogo, en el kardex y en cada renglon de
 * venta—; esto solo dice en que esta pensando quien teclea. Meter un "peso" en
 * `Unit` habria contaminado `UNIT_FAMILY`, `UNIT_PLURAL`, la columna `unit` de
 * `products` y todos los `Record<Unit, ...>` del sistema con un caso que no es
 * una manera de medir mercancia.
 */
export const MONEY_ENTRY = 'money';
export function isMoneyEntry(mode) {
    return mode === MONEY_ENTRY;
}
/** Como se llama el modo en el selector: "kilos", "gramos", "pesos". */
export function entryModeLabel(mode) {
    return isMoneyEntry(mode) ? 'pesos' : UNIT_PLURAL[mode];
}
/**
 * Las tres —o dos, o una— formas de decir cuanto lleva el cliente de ESTE
 * producto, empezando siempre por la unidad del precio.
 *
 * El dinero se ofrece con dos condiciones, y las dos son duras:
 *
 *   1. **Solo si el producto se pesa o se mide.** "Veinte pesos de refresco"
 *      son 1.33 refrescos, que no es una venta: es un renglon que nadie puede
 *      despachar y una existencia con decimales en un producto que se cuenta.
 *      Lo que se despacha a dinero es lo que sale del costal.
 *   2. **Solo si hay precio.** Sin precio no hay entre que dividir, asi que el
 *      modo no se dibuja en vez de dibujarse y no funcionar.
 */
export function entryModesFor(unit, priceCents) {
    const units = entryUnitsFor(unit);
    return unitIsWeighed(unit) && priceCents > 0 ? [...units, MONEY_ENTRY] : units;
}
/**
 * Lo tecleado como IMPORTE -> milesimas de la unidad del precio.
 *
 * El hermano de `qtyMilliFromEntry` para el otro modo, y la unica puerta por la
 * que un importe se vuelve cantidad en la captura. La aritmetica —y sobre todo
 * la eleccion de cual de las dos cantidades posibles cobra mas cerca de lo que
 * pidieron— vive en `qtyMilliFromMoneyCents`; aqui solo se interpreta el texto.
 *
 * Devuelve `null` si el texto no es un importe o si no hay precio. Nunca
 * adivina, igual que su hermano.
 */
export function qtyMilliFromMoneyEntry(text, priceCents) {
    const cents = parseMoneyToCents(text);
    if (cents === null || cents < 0)
        return null;
    return qtyMilliFromMoneyCents(cents, priceCents);
}
// ---------------------------------------------------------------------------
// Normalizacion
// ---------------------------------------------------------------------------
/** Minusculas, sin acentos y con los separadores vueltos espacio. */
function normalize(text) {
    return text
        .normalize('NFD')
        // `\p{Diacritic}` y no un rango literal: el rango se escribe con caracteres
        // combinantes INVISIBLES, y cualquier herramienta que "limpie" el archivo
        // se los lleva sin que se note que la funcion dejo de quitar acentos.
        // Descompuesta, la eñe tambien queda en ene, que es lo que hace que
        // "jalapeño" empate con "jalapeno".
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9.,]+/g, ' ')
        .trim();
}
/**
 * True si `term` aparece como palabra completa (o frase completa) dentro del
 * texto ya normalizado.
 *
 * Tiene que ser por palabra completa y no por subcadena: "sal" vive dentro de
 * "salchicha" y "res" dentro de "refresco". Con subcadena, un paquete de
 * salchichas se daria de alta por kilo.
 */
function hasTerm(normalized, term) {
    const padded = ` ${normalized} `;
    return padded.includes(` ${term} `);
}
function matchGroup(normalized, words) {
    for (const word of words) {
        if (hasTerm(normalized, word))
            return word;
    }
    return null;
}
/*
 * En farmacia la medida del nombre casi siempre es la DOSIS ("Paracetamol
 * 500 mg") o lo que trae la caja ("c/ 10 tabletas"). Por eso van `mg` y `mcg`
 * —antes que `ml`, para que "500 mg" no se quede sin leer— y las formas
 * farmaceuticas que se cuentan.
 */
const PRESENTATION_RE = /(\d+(?:[.,]\d+)?)\s*(mg|mcg|kgs?|kilogramos?|kilos?|grs?|gramos?|g|mls?|mililitros?|lts?|litros?|l|onzas?|oz|pzas?|piezas?|rollos?|sobres?|bolsas?|latas?|botellas?|paquetes?|tiras?|tabletas?|tabs?|capsulas?|caps?|grageas?|comprimidos?|ampolletas?|ampulas?|ovulos?|supositorios?|parches?|dosis)(?![a-z])/;
const MASS_TOKENS = /^(mg|mcg|kgs?|kilogramos?|kilos?|grs?|gramos?|g|onzas?|oz)$/;
const VOLUME_TOKENS = /^(mls?|mililitros?|lts?|litros?|l)$/;
/**
 * Encuentra la medida que el nombre ya trae escrita. `null` si no trae ninguna,
 * que es el caso del granel.
 */
export function parsePresentation(name) {
    const match = PRESENTATION_RE.exec(normalize(name));
    if (!match)
        return null;
    const [raw, digits, token] = match;
    if (digits === undefined || token === undefined)
        return null;
    const magnitude = Number(digits.replace(',', '.'));
    if (!Number.isFinite(magnitude) || magnitude <= 0)
        return null;
    const family = MASS_TOKENS.test(token)
        ? 'weight'
        : VOLUME_TOKENS.test(token)
            ? 'volume'
            : 'count';
    return { raw: raw.trim(), magnitude, family };
}
/**
 * Lo que en una farmacia se despacha, y como se cobra.
 *
 * EN UNA FARMACIA CASI NADA SE PESA. El vocabulario del sistema de origen
 * (Abarrotes Zuniga) proponia kilo para frijol, chile, queso o crema, y aqui
 * esas palabras mienten: "Sal de uvas" es un sobre, "Crema para rozaduras" un
 * tubo y "Aceite de almendras" un frasco. Con ese vocabulario la propuesta,
 * que se aplica sola, habria vuelto precio por kilo un medicamento.
 *
 * Asi que todas las palabras de aqui proponen PIEZA: la caja, el frasco, el
 * tubo, el sobre o la tableta suelta del exhibidor. Lo que hacen es explicar
 * por que, con las palabras del mostrador.
 *
 * La lista sigue siendo CORTA a proposito: una propuesta equivocada cuesta mas
 * que ninguna, porque se acepta sin leerla.
 */
const NAME_LEXICON = [
    {
        unit: 'pz',
        reason: 'Lo suelto se cobra por pieza: una tableta, un sobre o una tira.',
        words: ['suelto', 'suelta', 'sueltos', 'sueltas'],
    },
    {
        unit: 'pz',
        reason: 'Las medicinas se venden cerradas, por pieza: la caja, el frasco, el tubo o el sobre.',
        words: [
            'tableta', 'tabletas', 'capsula', 'capsulas', 'gragea', 'grageas', 'comprimido',
            'comprimidos', 'jarabe', 'suspension', 'gotas', 'solucion', 'pomada', 'crema',
            'gel', 'unguento', 'ampolleta', 'ampolletas', 'inyectable', 'supositorio',
            'supositorios', 'ovulo', 'ovulos', 'parche', 'parches', 'spray', 'aerosol',
            'sobre', 'sobres', 'frasco', 'tubo', 'blister',
        ],
    },
    {
        unit: 'pz',
        reason: 'El material de curacion se vende por pieza o por paquete cerrado.',
        words: [
            'gasa', 'gasas', 'venda', 'vendas', 'jeringa', 'jeringas', 'curita', 'curitas',
            'cubrebocas', 'guantes', 'termometro', 'torunda', 'torundas', 'aposito', 'apositos',
        ],
    },
];
/**
 * Lo unico que se despacha a granel en una farmacia, y se mide por litro.
 *
 * Solo lo consulta la regla 1: si el nombre dice "granel", esta lista decide
 * si es litro o kilo. Fuera de esa regla no propone nada.
 */
const BULK_LIQUID_WORDS = ['alcohol', 'cloro', 'desinfectante', 'glicerina', 'vaselina', 'aceite'];
/**
 * Nombres de categoria que delatan la unidad cuando la categoria todavia esta
 * vacia y no hay de donde sacar evidencia. En la farmacia las categorias son
 * padecimientos ("Gripa y tos", "Dolor") y todas se venden por pieza.
 */
const CATEGORY_LEXICON = [
    {
        unit: 'pz',
        reason: 'En la farmacia esta categoria se vende por pieza.',
        words: [
            'medicamentos', 'medicinas', 'analgesicos', 'antibioticos', 'antigripales', 'gripa',
            'tos', 'dolor', 'fiebre', 'estomago', 'gastritis', 'diarrea', 'vitaminas',
            'curacion', 'dermatologicos', 'genericos', 'patente', 'bebes', 'higiene',
        ],
    },
];
/**
 * Palabras que declaran granel sin ambiguedad, escriba lo que escriba despues.
 *
 * Solo "granel". En el sistema de origen tambien "suelto", "bulto" y "costal";
 * en una farmacia "suelto" quiere decir lo contrario —la tableta que se vende
 * por pieza fuera de su caja— y esta en `NAME_LEXICON` proponiendo pieza.
 */
const BULK_WORDS = ['granel'];
/**
 * Busca en el censo el renglon de la categoria elegida.
 *
 * Sin categoria no hay evidencia: el monton de "Sin categoria" junta productos
 * que no se parecen en nada, y una razon como "2 de los 4 productos de Sin
 * categoria se venden por kilo" no es un argumento, es ruido.
 */
export function evidenceFor(hints, categoryId, categoryName) {
    if (!hints || categoryId === null || !categoryName)
        return null;
    const hit = hints.find((h) => h.categoryId === categoryId);
    return hit ? { categoryName, unit: hit.unit, count: hit.count, total: hit.total } : null;
}
/** Minimo de productos para que una categoria pueda opinar. */
const EVIDENCE_MIN_TOTAL = 3;
/** Mayoria a partir de la cual la categoria opina. */
const EVIDENCE_MIN_SHARE = 0.6;
/** Mayoria a partir de la cual la propuesta se aplica sola. */
const EVIDENCE_STRONG_SHARE = 0.8;
/**
 * Propone la unidad de cobro. `null` significa "no tengo nada que decir", y la
 * pantalla no debe inventar nada en ese caso.
 *
 * El orden NO es arbitrario; va de lo mas especifico a lo mas general:
 *
 *   1. "granel" escrito en el nombre. Es una declaracion, no una pista.
 *   2. El gramaje del envase ("600 ml"). Si el nombre ya trae la medida, lo que
 *      se cobra es el envase.
 *   3. El vocabulario del ramo sobre el nombre. Habla de ESTE producto.
 *   4. Lo que la tienda ya captura en esa categoria. Habla de sus vecinos.
 *   5. El nombre de la categoria. Es lo unico que queda si la categoria esta
 *      vacia, y por eso nunca se aplica sola.
 *
 * EN UNA BODEGA (`profile: 'bulk'`) SOLO CORRE LA REGLA 4, la del censo de la
 * propia sucursal. Las otras cuatro son vocabulario de MENUDEO y ahi son falsas
 * con todas sus letras: "a granel se despacha y se pesa" describe la bascula del
 * mostrador, no el costal cerrado que sale en la camioneta; "«1 kg» es la medida
 * del envase, se vende cerrado" propone la pieza, que en una bodega no existe.
 * Una propuesta que no se puede aplicar es peor que ninguna —quien la lee cree
 * que la pantalla esta rota— asi que el motor se calla, que es la regla de
 * siempre: prefiere abstenerse a inventar.
 */
export function suggestUnit(input) {
    const name = normalize(input.name);
    const profile = input.profile ?? 'retail';
    const retail = profile === 'retail';
    // 1 -----------------------------------------------------------------------
    if (retail && matchGroup(name, BULK_WORDS)) {
        const liquid = matchGroup(name, BULK_LIQUID_WORDS) !== null;
        return {
            unit: liquid ? 'l' : 'kg',
            confidence: 'high',
            reason: liquid
                ? 'Lo dice el nombre: a granel se despacha y se mide por litro.'
                : 'Lo dice el nombre: a granel se despacha y se pesa.',
        };
    }
    // 2 -----------------------------------------------------------------------
    // En la farmacia lo cerrado va SIEMPRE por pieza, traiga lo que traiga:
    // "c/ 10 tabletas" es lo que hay dentro de la caja que se cobra, no un
    // paquete de diez cosas. (En el sistema de origen eso proponia `paq`.)
    const presentation = retail ? parsePresentation(input.name) : null;
    if (presentation) {
        return {
            unit: 'pz',
            confidence: 'high',
            reason: presentation.family === 'count'
                ? `"${presentation.raw}" es lo que trae la caja o el sobre: se cobra cerrado, por pieza.`
                : `"${presentation.raw}" es la dosis o el contenido, no lo que se cobra. Se vende cerrado, por pieza.`,
        };
    }
    // 3 -----------------------------------------------------------------------
    if (retail) {
        for (const group of NAME_LEXICON) {
            if (matchGroup(name, group.words)) {
                return { unit: group.unit, confidence: 'high', reason: group.reason };
            }
        }
    }
    // 4 -----------------------------------------------------------------------
    // La unica que sirve en las dos: no opina de mercancia, cuenta lo que esta
    // sucursal ya captura. Aun asi se comprueba que la unidad quepa en el perfil,
    // porque el censo puede traer un producto viejo de antes del cambio de tipo.
    const evidence = input.evidence;
    if (evidence && evidence.total >= EVIDENCE_MIN_TOTAL && unitAllowedIn(profile, evidence.unit)) {
        const share = evidence.count / evidence.total;
        if (share >= EVIDENCE_MIN_SHARE) {
            return {
                unit: evidence.unit,
                confidence: share >= EVIDENCE_STRONG_SHARE ? 'high' : 'medium',
                reason: `${evidence.count} de los ${evidence.total} productos de ${evidence.categoryName} ` +
                    `se venden por ${UNIT_SPOKEN[evidence.unit]}.`,
            };
        }
    }
    // 5 -----------------------------------------------------------------------
    const categoryName = retail ? normalize(input.categoryName ?? '') : '';
    if (categoryName) {
        for (const group of CATEGORY_LEXICON) {
            if (matchGroup(categoryName, group.words)) {
                return { unit: group.unit, confidence: 'medium', reason: group.reason };
            }
        }
    }
    return null;
}
/**
 * Aviso cuando la unidad elegida pelea con el nombre.
 *
 * Solo un caso, y es el que cuesta dinero: un producto cuyo nombre ya declara
 * su contenido ("Detergente en polvo 1 kg") capturado por peso o por medida.
 * Eso convierte una bolsa cerrada en granel: el precio se vuelve por kilo y la
 * caja empieza a pedir el peso de algo que nadie va a pesar.
 *
 * El caso contrario —granel capturado por pieza— no se avisa aqui: para eso
 * esta la propuesta, que es mas util porque ademas lo corrige.
 *
 * En una BODEGA no aplica y devuelve `null`: el aviso entero termina en "y esos
 * van por pieza", y ahi la pieza no existe. Ademas el caso no se da — "Maiz
 * blanco 50 kg" por bulto de 50 kg no es un conflicto, es exactamente lo que es.
 */
export function unitMismatchWarning(name, unit, profile = 'retail') {
    if (profile !== 'retail')
        return null;
    if (!unitIsWeighed(unit))
        return null;
    if (matchGroup(normalize(name), BULK_WORDS))
        return null;
    const presentation = parsePresentation(name);
    if (!presentation || presentation.family === 'count')
        return null;
    return (`El nombre ya dice "${presentation.raw}", asi que suena a producto cerrado y esos ` +
        `van por pieza. Deja ${UNIT_SPOKEN[unit]} solo si lo despachas tu y lo pesas.`);
}
//# sourceMappingURL=units.js.map